<template>
	<div class="big5demo">
		<div class="page-header">Big 5 Personality Test</div>
		<div class="page-intro" v-if="!showResults">COLPUNO offers a full Big 5 OCEAN personality test that helps you understand your personality and how it aligns with your career aspirations. Complete the test below to explore what you can learn from COLPUNO.</div>

		<div v-if="!showResults">
			<div class="questions">
				<div v-for="(q, qi) in questions" :key="qi" class="question-card">
					<div class="question-text">{{ q }}</div>
					<div class="answer-section" role="group" :aria-label="`Answer for question ${qi + 1}`">
						<button v-for="v in 5" :key="v" type="button" class="answer-btn"
							:class="{ selected: answers[qi] === v }" @touchstart.prevent="selectAnswer(qi, v)"
							@click="selectAnswer(qi, v)" :aria-pressed="answers[qi] === v ? 'true' : 'false'"
							:aria-label="`${v}`">
							{{ v }}
						</button>
					</div>
					<div class="scale-legend">
						<span>Strongly Disagree</span>
						<span>Strongly Agree</span>
					</div>
				</div>
			</div>
			<div class="actions">
				<button type="button" class="submit-btn" @click="submitTest" :disabled="!isComplete"
					:aria-disabled="!isComplete ? 'true' : 'false'">
					Complete Test
				</button>
			</div>
		</div>

		<transition name="slide-up">
			<div v-if="showResults">
				<div class="results-card" role="region" aria-label="Test results">
					<div class="results-title">Your results</div>
					<div class="results-subtitle">Openness to experience</div>
					<div class="results-layout">
						<div class="results-visual" aria-label="Results visualization">
							<img src="/big5-openness.svg" alt="Openness visualization" />
						</div>
						<div class="results-content">
							<div class="score-row">
								<div class="score-chip">{{ scorePercent }}%</div>
								<div class="score-hint">Higher numbers do not mean "better".</div>
							</div>
							<div class="trait-title">Openness to experience</div>
							<p class="trait-desc">{{ currentTexts.strength }}</p>
							<div class="watch-out">
								<div class="watch-out-title">WATCH OUT:</div>
								<p class="watch-out-desc">{{ currentTexts.watchOut }}</p>
							</div>
						</div>
					</div>
				</div>
				<div class="results-card promo-card" role="region" aria-label="Colpuno promotions">
					<div class="results-title">Waiting for you at COLPUNO</div>
					<div class="promo-subheader">
						<button type="button" class="promo-subnav" @click="prevPromo" aria-label="Previous item">‹</button>
						<div class="results-subtitle">{{ promoSlides[promoIndex].title }}</div>
						<button type="button" class="promo-subnav" @click="nextPromo" aria-label="Next item">›</button>
					</div>
					<div class="promo-slider">
						<DxMultiView :items="promoSlides" :selected-index="promoIndex" :swipe-enabled="isTouchDevice" :loop="true" :animation-enabled="true" height="auto" @selection-changed="onPromoSelectionChanged">
							<template #item="{ data }">
								<div class="promo-content">
									<div class="promo-text">{{ data.text }}</div>
									<div class="promo-visual">
										<img :src="data.img" :alt="data.title" />
									</div>
								</div>
							</template>
						</DxMultiView>
						<div class="promo-dots" role="tablist" aria-label="Promotion items">
							<button v-for="(s, i) in promoSlides" :key="i" type="button" class="promo-dot" :class="{ active: i === promoIndex }" @click="goPromo(i)" :aria-selected="i === promoIndex ? 'true' : 'false'" :aria-label="`Go to ${s.title}`"></button>
						</div>
					</div>
				</div>
				<div class="actions">
					<button type="button" class="reset-btn" @click="resetAll">Reset</button>
				</div>
			</div>
		</transition>
		<DxPopup v-model:visible="emailPopupVisible" :show-title="true" :show-close-button="true" title="Get your results" :hide-on-outside-click="true" :drag-enabled="false" :width="420" :height="300">
			<template #content>
				<div class="email-popup">
					<div class="email-text">Enter your email address and we will send you a special registration link to join COLPUNO today!</div>
					<DxTextBox placeholder="Email (optional)" mode="email" v-model:value="emailInput" />
					<div class="popup-actions-row">
						<DxButton text="Continue" type="default" @click="confirmEmailAndShowResults" />
						<DxButton text="No, just show me the results" @click="skipEmailAndShowResults" />
					</div>
				</div>
			</template>
		</DxPopup>
	</div>

</template>

<script setup>
/* eslint-disable */
import { ref, computed, nextTick, inject } from 'vue';
import { DxMultiView } from 'devextreme-vue/multi-view';
import { DxPopup } from 'devextreme-vue/popup';
import { DxTextBox } from 'devextreme-vue/text-box';
import { DxButton } from 'devextreme-vue/button';

const isTouchDevice = computed(() => {
	if (typeof window === 'undefined') return false;
	const nav = window.navigator || {};
	return (
		'ontouchstart' in window ||
		(nav.maxTouchPoints && nav.maxTouchPoints > 0) ||
		(nav.msMaxTouchPoints && nav.msMaxTouchPoints > 0) ||
		(!!window.matchMedia && window.matchMedia('(pointer: coarse)').matches === true)
	);
});

const questions = ref([
	'I have a vivid imagination.',
	'I enjoy learning new things and trying different experiences.',
	'I am not interested in abstract ideas.',
	'I often spend time reflecting on things.',
	'Following the same routine most days helps me feel in control.',
]);
// Openness to Experience: HHLHL

const answers = ref([null, null, null, null, null]);
// const answers = ref([3, 3, 3, 3, 3]);

const showResults = ref(false);
const emailPopupVisible = ref(false);
const emailInput = ref('');
const $http = inject('$http');

function selectAnswer(questionIndex, value) {
	answers.value[questionIndex] = value;
}

const isComplete = computed(() => answers.value.every(v => v !== null));

function revealResults() {
	showResults.value = true;
	// Scroll to top instantly after showing results.
	nextTick(() => {
		try {
			const dxContainer = document.querySelector('.dx-scrollable-container');
			if (dxContainer && typeof dxContainer.scrollTo === 'function') {
				dxContainer.scrollTo({ top: 0, behavior: 'auto' });
			} else {
				window.scrollTo({ top: 0, behavior: 'auto' });
			}
		} catch (_err) { /* no-op */ }
	});
}

function submitTest() {
	emailPopupVisible.value = true;
}

async function confirmEmailAndShowResults() {
	// Optionally handle emailInput.value here (e.g., send to backend)
	try {
		const email = (emailInput.value || '').trim();
		await $http.post('/survey', {
			type: 'big5_demo',
			raffle: 0,
			results: JSON.stringify({ email }),
		});
	} catch (_err) { /* no-op */ }
	emailPopupVisible.value = false;
	revealResults();
}

function skipEmailAndShowResults() {
	emailPopupVisible.value = false;
	revealResults();
}

function resetAll() {
	window.location.reload();
}

// Scoring logic
const reversedIndices = new Set([2, 4]); // 0-based: 3rd and 5th questions
const adjustedAnswers = computed(() =>
	answers.value.map((v, i) => (v == null ? 0 : reversedIndices.has(i) ? 6 - v : v))
);

const sumAdjusted = computed(() => adjustedAnswers.value.reduce((a, b) => a + b, 0));
const meanAdjusted = computed(() => (answers.value.every(v => v != null) ? sumAdjusted.value / 5 : 0));
const scorePercent = computed(() => (answers.value.every(v => v != null) ? Math.round((sumAdjusted.value / 25) * 100) : 0));

const thresholds = [1.5, 2.49, 3.49, 4.49, 5.1];
const categoryLabels = ['veryLow', 'low', 'medium', 'high', 'veryHigh'];

const openness = {
	veryLow: {
		strength:
			'You deliberately lean towards practicality and tradition, providing stability at work and reliability in relationships due to your structured outlook and cautious approach to change.',
		watchOut:
			'Open yourself carefully to novelty in certain situations such as new learning environments or discussions to avoid missing valuable insights and opportunities for personal advancement.',
	},
	low: {
		strength:
			'You intentionally value practicality and reliability, typically seeing projects through effectively by honoring traditional methods and choosing proven ideas over abstract, unconventional approaches.',
		watchOut:
			'While thoughtfully embracing familiar practices, actively seeking exposure to occasional fresh perspectives can enrich relationships and promote adaptability to unforeseen situations.',
	},
	medium: {
		strength:
			'You often balance exploring new ideas with appreciating established routines, allowing you to contribute fresh perspectives while maintaining practicality in both professional and creative situations.',
		watchOut:
			'Consider proactively challenging your familiar routines occasionally to unlock further personal growth and creative potential when approaching new projects or unfamiliar experiences.',
	},
	high: {
		strength:
			'You naturally embrace diverse perspectives and experiences, fueling creativity in activities such as artistic projects, problem-solving, and cross-cultural engagements.',
		watchOut:
			'Although you eagerly pursue new ideas and adventures, be mindful of situations requiring practicality and consistency to balance innovation with effective implementation.',
	},
	veryHigh: {
		strength:
			'You naturally embrace imaginative thinking, pioneering novel ideas and continuously seeking a broad array of intellectual experiences to inspire creativity and innovation.',
		watchOut:
			'Given your adventurous imagination, carefully balance exploration of novel ideas with practical commitments to effectively sustain productivity and focus.',
	},
};

const categoryIndex = computed(() => {
	const m = meanAdjusted.value; // on 1..5 scale
	for (let i = 0; i < thresholds.length; i++) {
		if (m < thresholds[i]) return i;
	}
	return thresholds.length - 1;
});

const categoryKey = computed(() => categoryLabels[categoryIndex.value] || 'medium');
const currentTexts = computed(() => openness[categoryKey.value]);

// Promo slider content
const promoSlides = ref([
	{ title: 'Full Big 5 Personality Test', text: 'Explore all five OCEAN traits and get detailed insights for each category.', img: '/big5_demo_1.png' },
	{ title: 'Personality Summary and Key Strengths', text: 'Get a concise summary with your standout strengths ready to be used on your next job application.', img: '/big5_demo_2.png' },
	{ title: 'Psychometric Job Fit', text: 'See which roles align with your behavioral profile. Explore insights into work environments, career aspirations, and more.', img: '/big5_demo_3.png' },
]);

const promoIndex = ref(0);
function nextPromo() { 
	console.log('nextPromo', promoIndex.value);
	promoIndex.value = promoIndex.value + 1;
	if (promoIndex.value >= promoSlides.value.length) {
		promoIndex.value = 0;
	}
	// promoIndex.value = (promoIndex.value + 1) % promoSlides.value.length;
}
function prevPromo() { promoIndex.value = (promoIndex.value - 1 + promoSlides.value.length) % promoSlides.value.length; }
function goPromo(i) { promoIndex.value = i; }

function onPromoSelectionChanged(e) {
	// Keep local index in sync when user swipes
	try {
		promoIndex.value = e.component.option('selectedIndex');
	} catch (_err) { /* no-op */ }
}

</script>

<style lang="scss">
@import url('https://fonts.googleapis.com/css2?family=Magra:wght@400;700&family=Open+Sans:wght@400;600;700&display=swap');

.big5demo {
	padding: 16px;
	margin: 0 auto;
	max-width: 720px;
	-webkit-font-smoothing: antialiased;
	-moz-osx-font-smoothing: grayscale;
	font-family: "Open Sans", sans-serif;
}

.page-header,
.results-title,
.results-subtitle,
.trait-title,
.watch-out-title {
	font-family: "Magra", sans-serif;
}

.page-header {
	font-size: 32px;
	font-weight: 700;
	text-align: center;
	letter-spacing: 0.3px;
	margin-bottom: 16px;
	color: #173AA8;
	position: relative;
	padding-bottom: 6px;
}

.page-header::after {
	content: "";
	position: relative;
	display: block;
	height: 4px;
	width: 140px;
	margin: 8px auto 0;
	background: linear-gradient(90deg, #173AA8, #aebae0);
	border-radius: 9999px;
}

.page-intro {
	font-size: 14px;
	color: #4a5568;
	text-align: center;
	margin: 8px 0 16px;
}

.questions {
	display: flex;
	flex-direction: column;
	gap: 24px;
}

.question-card {
	display: flex;
	flex-direction: column;
	gap: 10px;
}

.question-text {
	font-size: 16px;
	line-height: 1.35;
}

/* Answer section styling per requirement */
.answer-section {
	background: #fae7e9;
	color: #de606e;
	border-radius: 12px;
	padding: 0;
	display: flex;
	justify-content: space-between;
	align-items: stretch;
	gap: 8px;
}

.answer-btn {
	flex: 1 1 0;
	height: 48px;
	border: none;
	border-radius: 10px;
	background: transparent;
	color: inherit;
	font-size: 16px;
	font-weight: 600;
	-touch-action: manipulation;
	-webkit-tap-highlight-color: transparent;
	outline: none;
	transition: background-color 120ms ease, color 120ms ease;
	cursor: pointer;
}

.answer-btn.selected {
	background: rgb(206, 17, 39);
	color: #fff;
}

.answer-btn.selected:hover {
	background: rgb(206, 17, 39);
	color: #fff;
}

.answer-btn:hover {
	background: rgb(146, 12, 28);
	color: #fff;
}

.answer-btn:active {
	transform: scale(0.98);
}

.scale-legend {
	display: flex;
	justify-content: space-between;
	font-size: 12px;
	color: #de606e;
	padding: 0 4px;
}

.actions {
	margin-top: 20px;
	display: flex;
	justify-content: center;
}

.submit-btn {
	background: #173AA8;
	color: #fff;
	border: none;
	border-radius: 9999px;
	padding: 12px 18px;
	font-size: 16px;
	font-weight: 700;
	cursor: pointer;
	min-width: 200px;
	box-shadow: 0 2px 6px rgba(23, 58, 168, 0.25);
	transition: transform 120ms ease, box-shadow 120ms ease, opacity 120ms ease;
}

.submit-btn:active {
	transform: scale(0.98);
}

.submit-btn:hover {
	box-shadow: 0 4px 12px rgba(23, 58, 168, 0.35);
}

.submit-btn:disabled {
	opacity: 0.7;
	cursor: not-allowed;
	box-shadow: none;
}

.reset-btn {
	background: #fff;
	color: #173AA8;
	border: 2px solid #173AA8;
	border-radius: 9999px;
	padding: 12px 18px;
	font-size: 16px;
	font-weight: 700;
	cursor: pointer;
	min-width: 200px;
	transition: transform 120ms ease, opacity 120ms ease;
}

.reset-btn:active {
	transform: scale(0.98);
}

/* Transitions */
.slide-up-enter-active,
.slide-up-leave-active {
	transition: 750ms ease;
	transition-property: opacity, transform;
}

.slide-up-enter-from,
.slide-up-leave-to {
	opacity: 0;
	transform: translateY(36px);
}

.slide-up-enter-to,
.slide-up-leave-from {
	opacity: 1;
	transform: translateY(0);
}

.results-card {
	background: #f7f9ff;
	border: 1px solid #e4e9fb;
	border-radius: 12px;
	padding: 16px;
	color: #173AA8;
}

.results-title {
	font-weight: 700;
	margin-bottom: 6px;
	font-size: 24px;
	text-align: center;
}

.results-subtitle {
	font-size: 18px;
	font-weight: 600;
	color: #4a5568;
	text-align: center;
	margin-bottom: 16px;
}

.results-layout {
	display: grid;
	grid-template-columns: 60% 40%;
}

.results-visual {
	background: transparent;
}

.results-visual svg,
.results-visual img {
	width: 100%;
	height: auto;
	display: block;
}

.results-body {
	color: #334;
}

.results-content {
	color: rgb(10, 24, 71);
}

.score-row {
	display: flex;
	align-items: center;
	justify-content: space-evenly;
	gap: 2px;
	margin-bottom: 10px;
}

.score-hint {
	font-size: 11px;
	color: #4a5568;
	font-style: italic;
	text-align: right;
}

.score-chip {
	background: rgb(232, 235, 246);
	color: rgb(10, 24, 71);
	display: inline-block;
	padding: 6px 10px;
	border-radius: 9999px;
	font-weight: 700;
}

.trait-title {
	font-size: 18px;
	font-weight: 700;
	margin-bottom: 8px;
}

.trait-desc {
	font-size: 14px;
	line-height: 1.5;
	margin-bottom: 14px;
}

.watch-out-title {
	color: rgb(206, 17, 39);
	font-size: 13px;
	font-weight: 800;
	letter-spacing: 0.5px;
	margin-bottom: 6px;
}

.watch-out-desc {
	font-size: 13px;
	line-height: 1.5;
	margin-top: 0;
}

/* Promo (advert) section */
.promo-card {
	margin-top: 16px;
}

.promo-subheader {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 0;
	margin-bottom: 12px;
	position: relative;
	padding: 0 52px; /* keep title centered with fixed arrow width */
	height: 44px;
}

.promo-subheader .results-subtitle {
	margin: 0;
}

.promo-subnav {
	background: #fff;
	color: #173AA8;
	border: 2px solid #173AA8;
	border-radius: 9999px;
	width: 40px;
	height: 40px;
	font-size: 18px;
	font-weight: 700;
	cursor: pointer;
	transition: transform 120ms ease, opacity 120ms ease;
	position: absolute;
	top: 0;
	transform: translateY(5px);
}

.promo-subnav:active {
	transform: scale(0.98);
}

.promo-subnav:first-child { left: 0; }
.promo-subnav:last-child { right: 0; }

.promo-slider {
	display: grid;
	grid-template-columns: 1fr;
	align-items: center;
	gap: 8px;
}

.promo-content {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 8px;
}

.promo-text {
	font-size: 14px;
	color: #4a5568;
	text-align: center;
	font-style: italic;
}

.promo-visual img {
	width: 100%;
	height: auto;
	display: block;
	border-radius: 8px;
}

.promo-dots {
	display: flex;
	justify-content: center;
	gap: 6px;
	margin-top: 4px;
}

.promo-dot {
	width: 8px;
	height: 8px;
	border-radius: 9999px;
	border: none;
	background: #c7d2fe;
	cursor: pointer;
}

.promo-dot.active {
	background: #173AA8;
}

/* Email popup alignment */
.email-popup {
	display: flex;
	flex-direction: column;
	gap: 12px;
	align-items: center;
	text-align: center;

	.email-text {
		font-size: 14px;
		color: #4a5568;
		text-align: center;
		font-style: italic;
	}
	.dx-texteditor {
		width: 100%;
		max-width: 320px;
	}
	.popup-actions-row {
		display: flex;
		flex-direction: column;
		gap: 8px;
		align-items: center;
	}
}

/* Small screens tweaks */
@media (max-width: 500px) {
	.answer-btn {
		height: 44px;
		font-size: 15px;
	}

	.page-header {
		font-size: 24px;
	}

	.question-text {
		font-size: 15px;
	}

	.results-layout {
		grid-template-columns: 1fr;
	}

	.promo-slider {
		grid-template-columns: 1fr;
	}

	.promo-subnav {
		width: 36px;
		height: 36px;
	}

	.promo-subheader {
		padding: 0 44px;
	}
}
</style>